import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ImageryImportTarget, ImageryOptionKind } from "../../../../hooks/use-imagery";

interface Props {
  optionKind: ImageryOptionKind;
  disabled?: boolean;
  onImport: (target: ImageryImportTarget) => void;
}

const TARGETS: { target: ImageryImportTarget; label: string }[] = [
  { target: "aerial", label: "Set as aerial photo" },
  { target: "flood", label: "Set as flood hazards" },
  { target: "photos", label: "Add to photos" },
];

export function ImageryImportMenu({ optionKind, disabled, onImport }: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-input bg-white px-2 py-1 text-xs disabled:opacity-50"
        data-testid={`imagery-import-menu-${optionKind}`}
      >
        Import
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg"
          data-testid={`imagery-import-menu-list-${optionKind}`}
        >
          {TARGETS.map((t) => (
            <button
              key={t.target}
              type="button"
              onClick={() => {
                onImport(t.target);
                setOpen(false);
              }}
              className="block w-full px-2 py-1.5 text-left text-xs hover:bg-muted"
              data-testid={`imagery-import-${optionKind}-${t.target}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
